import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { deleteShare } from '@/services/share.service';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useDeleteShareMutation = () => {
  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const deleteShareWithAuth = useAuthenticatedQueryFn(deleteShare);

  const deleteShareMutation = useMutation({
    mutationFn: async ({ nodeId, shareId }: { nodeId: string; shareId: string; lastShare?: boolean }) => {
      await deleteShareWithAuth({ nodeId, shareId });
    },
    onSuccess: (_data, { shareId, lastShare: isLastShare }) => {
      toast({
        title: tToasts('deleteShareMutation.title'),
        description: tToasts('deleteShareMutation.description', { id: shareId }),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.NodeShareList] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ShareList] });

      if (isLastShare) {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.TreeSearch], exact: false });
      }
    },
    onError: (err) => {
      errorLog('Action failed:', err);

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  return { deleteShareMutation };
};
